import React, { useCallback, useEffect, useMemo } from 'react'
import cx from 'clsx'
import { NavLink, generatePath, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

import ContentLock from '@components/ContentLock'
import Tooltip from '@components/Tooltip'
import OrganizationMenu from '../OrganizationMenu'
import Divider from '@components/Divider'

import { useGetMyAccount } from '@queries/Account'
import { useIsMobile } from '@src/helpers'
import useWindowSize from '@helpers/hooks/useWindowSize'

import type { NavItem } from '../menuItems'
import { AP_ITEMS, AR_ITEMS, NAV_ITEMS, CONTRACT_ITEMS } from '../menuItems'
import type { FilteredNavItem } from '../utils'
import { filterNavItems, useWindowControlsOverlay } from '../utils'
import { useGetShowCustomers } from '@helpers/hooks/useGetShowCustomers'
import routes from '@src/Routes/routes'
import useStore from '@src/store'
import { useGetTeamSubscription } from '@queries/Billing'
import { useGetMaintenanceBanner } from '@helpers/hooks/maintenace/useGetMaintenanceBanner'
import { needsOnboarding } from '@helpers/hooks/useNeedsOnboarding'
import { REGION, SHOW_MAINTENANCE_BANNER } from '@src/constants'

import { ContactParty } from '@models/ApproveitAccounting'
import { Region } from '@type/common'

import styles from './index.module.css'

interface NavigationProps {
  children?: React.ReactNode
  isCollapsed: boolean
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
}

const NavigationBlock = ({
  items,
  isCollapsed,
  title,
  className,
  name,
}: {
  items: FilteredNavItem<NavItem>[]
  isCollapsed: boolean
  title?: string
  className?: string
  name?: string
}) => {
  // The group is locked when every entry in it is: the lock state comes
  // per item from /my-account, so this needs no second read of the plan.
  const groupLocked = items.length > 0 && items.every((item) => item.locked)
  const groupPaywallPath = groupLocked ? items[0].paywallPath : undefined
  return items.length ? (
    <div className={twMerge(cx('pt-3 flex flex-col text-nowrap', className))} data-name={name}>
      {title && (
        <div
          className={cx(
            'flex justify-between items-center pl-5 pr-3 text-sm font-medium text-grey_1 transition-opacity duration-400 mb-1',
            isCollapsed ? 'opacity-0' : 'opacity-100',
          )}
        >
          {title}
          {groupPaywallPath && <ContentLock to={groupPaywallPath} />}
        </div>
      )}
      {items.map((item) => (
        <NavigationLink key={item.to} {...item} isCollapsed={isCollapsed} />
      ))}
    </div>
  ) : null
}

const NavigationLink: React.FC<FilteredNavItem<NavItem>> = ({
  to,
  disabled,
  icon: Icon,
  label,
  locked,
  paywallPath,
  lockTooltip,
  isCollapsed,
}) => {
  const { data: user } = useGetMyAccount()
  const hasAccess = !needsOnboarding(user)

  if (!to) return null
  // A plan lock disables the row (only the lock badge navigates, to the
  // paywall). A lock with no paywall behind it — the assistant's beta lock —
  // dims the row but keeps it clickable: the page carries the pitch.
  const isDisabled = (locked && !!paywallPath) || disabled || !hasAccess
  const isDimmed = isDisabled || locked
  return (
    <>
      <NavLink
        to={locked && paywallPath ? paywallPath : to}
        className={({ isActive }) =>
          cx(
            (isActive || (window.location.pathname === '/' && to === '/dashboard')) && styles.active,
            'flex items-center px-2 group',
            isDisabled && styles.disabled,
          )
        }
        onContextMenu={(e) => e.stopPropagation()}
        data-tooltip-content={isCollapsed ? label : ''}
        data-tooltip-id={to}
      >
        <div
          className={cx(
            styles.leftNavLink,
            isCollapsed && styles.leftNavLinkCollapsed,
            !isDisabled &&
              'group-hover:bg-soft_lavender group-active:outline group-active:outline-blue_lavender group-active:bg-blue_lavender',
          )}
        >
          <Icon
            className={cx(
              styles.navIcon,
              !isDisabled && 'group-hover:scale-105 group-active:scale-95',
              isDimmed && 'opacity-60',
            )}
            stroke={1.5}
          />
          <div className="flex w-full justify-between">
            <span className={cx(isDimmed && 'opacity-60')}>{label}</span>
          </div>
          {locked && <ContentLock tooltipText={paywallPath ? undefined : lockTooltip} />}
        </div>
      </NavLink>
      {isCollapsed && <Tooltip id={to} place="right" offset={10} />}
    </>
  )
}

const BURGER_BREAKPOINT = 768

// Plan-locked entries stay in the sidebar, locked and pointing at the
// paywall (the upsell surface). Module scope so the memos below can list
// it without re-creating it per render.
const INCLUDE_LOCKED = { includeLocked: true }

const LeftNav: React.FC<NavigationProps> = ({ isCollapsed = true, setIsCollapsed, children, className }) => {
  const { t } = useTranslation('common', { keyPrefix: 'navigation' })

  const isSettingsPage = useLocation().pathname.includes('/settings')
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const isMobile = useIsMobile()
  const { width } = useWindowSize()
  const isWindowControlsOverlay = useWindowControlsOverlay()
  const [maintenace] = useGetMaintenanceBanner()

  const { data: user } = useGetMyAccount()
  const { data: subscription } = useGetTeamSubscription({ enabled: !!user?.team?.subscription })

  const showCustomers = useGetShowCustomers()
  const isOnboarding = useStore.use.isOnboarding()

  const hasAccess = !needsOnboarding(user)

  const handleMouseEnter = useCallback(() => {
    if (isWindowControlsOverlay) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(false)
      }, 333)
    }
  }, [isWindowControlsOverlay])

  const handleMouseLeave = useCallback(() => {
    if (isWindowControlsOverlay) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(true)
      }, 100)
    }
  }, [isWindowControlsOverlay])

  useEffect(() => {
    if (isWindowControlsOverlay) {
      setIsCollapsed(true)
    }
  }, [isWindowControlsOverlay])

  const commonItems = useMemo(() => (user ? filterNavItems(NAV_ITEMS, user, undefined, INCLUDE_LOCKED) : []), [user])
  const apItems = useMemo(() => {
    return user
      ? filterNavItems(
          AP_ITEMS,
          user,
          { [generatePath(routes.private.contacts, { contactParty: ContactParty.vendor })]: showCustomers },
          INCLUDE_LOCKED,
        )
      : []
  }, [user, showCustomers])
  const arItems = useMemo(() => {
    return user
      ? filterNavItems(
          AR_ITEMS,
          user,
          { [generatePath(routes.private.contacts, { contactParty: ContactParty.customer })]: showCustomers },
          INCLUDE_LOCKED,
        )
      : []
  }, [user, showCustomers])
  const contractItems = useMemo(() => {
    return user ? filterNavItems(CONTRACT_ITEMS, user, undefined, INCLUDE_LOCKED) : []
  }, [user])

  const hasBanner = subscription?.is_trial || (!subscription?.is_trial && SHOW_MAINTENANCE_BANNER && maintenace.show)

  return (
    <>
      {hasAccess && (
        <div
          className={cx(
            className,
            styles.leftNav,
            isCollapsed ? styles.collapsed : styles.expanded,
            (isMobile || width < BURGER_BREAKPOINT) && !isWindowControlsOverlay ? 'hidden' : 'flex',
            isSettingsPage && isCollapsed && 'w-0 px-0 transition-all',
          )}
          onContextMenu={(e) => e.preventDefault()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            height: isWindowControlsOverlay
              ? 'calc(100vh - env(titlebar-area-height, 56px))'
              : `calc(100vh - ${!isOnboarding && hasBanner ? 104 : 56}px)`,
          }}
        >
          <OrganizationMenu classes={{ companyItem: cx(isCollapsed && 'ml-10') }} />
          <div className="w-full px-4">
            <Divider className="!my-1.5 px-2" />
          </div>
          <NavigationBlock items={commonItems} isCollapsed={isCollapsed} className="pt-0" name="common" />
          <NavigationBlock
            items={apItems}
            isCollapsed={isCollapsed}
            title={t('accountsPayable')}
            name="accountsPayable"
          />
          <NavigationBlock
            items={arItems}
            isCollapsed={isCollapsed}
            title={t('accountsReceivable')}
            name="accountsReceivable"
          />
          <NavigationBlock
            items={contractItems}
            isCollapsed={isCollapsed}
            title={t('contractsManagement')}
            name="contractsManagement"
          />
          {(REGION === Region.eu || REGION === Region.sg) && (
            <div className="w-full flex flex-col justify-end grow px-4">
              <div className="flex items-center gap-5">
                <img
                  src={REGION === Region.eu ? '/EU-region.svg' : '/SG-region.svg'}
                  alt={REGION === Region.eu ? 'EU' : 'SG'}
                  className="w-6 h-6"
                />
                <span className="whitespace-nowrap">Region {REGION === Region.eu ? 'EU' : 'SG'}</span>
              </div>
            </div>
          )}
        </div>
      )}
      {children}
    </>
  )
}
export default LeftNav
